import * as ApolloLib from "apollo-angular";
import { EMPTY, Observable, map } from "rxjs";

import { Injectable, inject } from "@angular/core";

import {
  FirewallListQueryGQL,
  FirewallListQueryQuery,
  IpListQueryGQL,
  IpListQueryQuery,
  IpPrefixesListQueryGQL,
  IpPrefixesListQueryQuery,
  L2vpnListQueryGQL,
  L2vpnListQueryQuery,
  L3vpnListQueryGQL,
  L3vpnListQueryQuery,
  LightPathListQueryGQL,
  LightPathListQueryQuery,
  PortAccessCustomerListQueryGQL,
  PortAccessCustomerListQueryQuery,
  WiredSubscriptionListQueryGQL,
  WiredSubscriptionListQueryQuery,
  WirelessSubscriptionListQueryGQL,
  WirelessSubscriptionListQueryQuery,
} from "../apis/networkdashboard/graphql/graphql";
import { ProductType } from "../helpers/enums/productType";
import { AuthService } from "./authservice";

export type SubscriptionListItem =
  | FirewallListQueryQuery["subscriptions"]["page"][number]
  | IpListQueryQuery["subscriptions"]["page"][number]
  | L2vpnListQueryQuery["subscriptions"]["page"][number]
  | L3vpnListQueryQuery["subscriptions"]["page"][number]
  | LightPathListQueryQuery["subscriptions"]["page"][number]
  | PortAccessCustomerListQueryQuery["subscriptions"]["page"][number]
  | WiredSubscriptionListQueryQuery["subscriptions"]["page"][number]
  | WirelessSubscriptionListQueryQuery["subscriptions"]["page"][number];

@Injectable({ providedIn: "root" })
export class SubscriptionListService {
  private auth = inject(AuthService);

  private firewallGQL = inject(FirewallListQueryGQL);
  private ipGQL = inject(IpListQueryGQL);
  private ipPrefixesGQL = inject(IpPrefixesListQueryGQL);
  private l3vpnGQL = inject(L3vpnListQueryGQL);
  private l2vpnGQL = inject(L2vpnListQueryGQL);
  private lightpathGQL = inject(LightPathListQueryGQL);
  private portGQL = inject(PortAccessCustomerListQueryGQL);
  private wirelessGQL = inject(WirelessSubscriptionListQueryGQL);
  private wiredGQL = inject(WiredSubscriptionListQueryGQL);

  fetchFirewall() {
    return this.fetchItems(this.firewallGQL, (d: FirewallListQueryQuery) => d.subscriptions.page);
  }

  fetchIp() {
    return this.fetchItems(this.ipGQL, (d: IpListQueryQuery) => d.subscriptions.page);
  }

  fetchIpPrefixes() {
    return this.fetchItems(this.ipPrefixesGQL, (d: IpPrefixesListQueryQuery) => d.ipPrefixList.page);
  }

  fetchL3vpn() {
    return this.fetchItems(this.l3vpnGQL, (d: L3vpnListQueryQuery) => d.subscriptions.page);
  }

  fetchL2vpn() {
    return this.fetchItems(this.l2vpnGQL, (d: L2vpnListQueryQuery) => d.subscriptions.page);
  }

  fetchLightPath() {
    return this.fetchItems(this.lightpathGQL, (d: LightPathListQueryQuery) => d.subscriptions.page);
  }

  fetchPort() {
    return this.fetchItems(this.portGQL, (d: PortAccessCustomerListQueryQuery) => d.subscriptions.page);
  }

  fetchWireless() {
    // Wireless items are labelled by customerDescription (the location name), so sort on that.
    return this.fetchItems(
      this.wirelessGQL,
      (d: WirelessSubscriptionListQueryQuery) => d.subscriptions.page,
      (a, b) => a.customerDescription.localeCompare(b.customerDescription),
    );
  }

  fetchWired() {
    return this.fetchItems(this.wiredGQL, (d: WiredSubscriptionListQueryQuery) => d.subscriptions.page);
  }

  fetch(productType: string): Observable<SubscriptionListItem[]> {
    switch (productType) {
      case ProductType.Firewall:
        return this.fetchFirewall();
      case ProductType.IP:
        return this.fetchIp();
      case ProductType.L3VPN:
        return this.fetchL3vpn();
      case ProductType.L2VPN:
        return this.fetchL2vpn();
      case ProductType.LightPath:
        return this.fetchLightPath();
      case ProductType.Port:
        return this.fetchPort();
      case ProductType.Wireless:
        return this.fetchWireless();
      case ProductType.Wired:
        return this.fetchWired();
      default:
        return EMPTY;
    }
  }

  private fetchItems<TData, TItem extends { subscriptionId: string }>(
    gql: ApolloLib.Query<TData, { customer: string }>,
    extract: (data: TData) => TItem[],
    compare: (a: TItem, b: TItem) => number = (a, b) =>
      String(a.subscriptionId).localeCompare(String(b.subscriptionId)),
  ): Observable<TItem[]> {
    return gql
      .fetch({ variables: { customer: this.auth.viewingCustomer().customerId } })
      .pipe(map(({ data }) => (data ? [...extract(data)].sort(compare) : [])));
  }
}
